/* Desktop PDP/OOLP flavour-step mirror. Load after eleat-brand-qa.js.
 * The original CTA remains the only purchase implementation; this button
 * delegates one user activation to it. The existing <=767px bar is untouched.
 */
(() => {
  const controllers = new Map();
  const desktop = window.matchMedia('(min-width: 768px)');
  const overlaySelector = '.cart-aside, .product-template__models, .build-a-bundle__models, [aria-modal="true"], dialog';
  let serial = 0;

  const text = (node) => node?.textContent.replace(/\s+/g, ' ').trim() || '';
  const setText = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const setAttribute = (node, name, value) => { if (node.getAttribute(name) !== value) node.setAttribute(name, value); };
  const shown = (node) => {
    if (!node || node.hidden || !node.getClientRects().length) return false;
    const style = getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };
  const overlayOpen = () => !!document.querySelector('.cart-aside--active') ||
    Array.from(document.querySelectorAll(overlaySelector)).some((node) => {
      if (node.matches('.cart-aside')) return false;
      if (node.matches('dialog') && !node.open) return false;
      return node.getAttribute('aria-hidden') !== 'true' && shown(node);
    });

  function updateBodyState() {
    const active = Array.from(controllers.values()).some((controller) => controller.active());
    if (document.body.classList.contains('eleat-desktop-purchase-active') !== active) {
      document.body.classList.toggle('eleat-desktop-purchase-active', active);
    }
  }

  function boot() {
    document.querySelectorAll('.component-product-info-alt').forEach((root) => {
      if (controllers.has(root)) return;
      const original = root.querySelector('.product-template__flavour-cta');
      const flavour = root.querySelector('.product-template__flavour');
      const plans = root.querySelector('.product-template__plan[role="radiogroup"]');
      if (!original || !flavour || !plans) return;

      const id = `eleat-desktop-purchase-${++serial}`;
      const bar = document.createElement('div');
      bar.className = 'eleat-desktop-purchase';
      bar.hidden = true;
      const inner = document.createElement('div');
      inner.className = 'eleat-desktop-purchase__inner';
      const details = document.createElement('div');
      details.className = 'eleat-desktop-purchase__details';
      const heading = document.createElement('p');
      heading.className = 'eleat-desktop-purchase__heading';
      const meter = document.createElement('div');
      meter.className = 'eleat-desktop-purchase__meter';
      meter.setAttribute('role', 'progressbar');
      meter.setAttribute('aria-label', 'Pack selection progress');
      meter.setAttribute('aria-valuemin', '0');
      const meterFill = document.createElement('span');
      meter.append(meterFill);
      const total = document.createElement('div');
      total.className = 'eleat-desktop-purchase__total';
      const totalLabel = document.createElement('span');
      totalLabel.className = 'eleat-desktop-purchase__total-label';
      totalLabel.textContent = 'Total';
      const prices = document.createElement('div');
      prices.className = 'eleat-desktop-purchase__prices';
      const compare = document.createElement('s');
      compare.className = 'eleat-desktop-purchase__compare';
      const price = document.createElement('span');
      price.className = 'eleat-desktop-purchase__price';
      prices.append(compare, price);
      total.append(totalLabel, prices);
      const summary = document.createElement('p');
      summary.className = 'eleat-desktop-purchase__summary';
      summary.id = `${id}-summary`;
      // The original live summary already announces changes; avoid a duplicate live region.
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'eleat-desktop-purchase__button';
      button.disabled = true;
      button.setAttribute('aria-describedby', summary.id);
      const buttonLabel = document.createElement('span');
      buttonLabel.className = 'eleat-desktop-purchase__label';
      button.append(buttonLabel);
      const buttonIcons = Array.from(original.querySelectorAll(':scope > .eleat-cta-cart, :scope > .eleat-cta-lock')).map((source) => {
        const icon = source.cloneNode(true);
        icon.classList.remove('cta-icon');
        icon.classList.add('eleat-desktop-purchase__icon');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('focusable', 'false');
        icon.setAttribute('hidden', '');
        button.append(icon);
        return { source, icon };
      });
      details.append(heading, meter);
      inner.append(details, total, button, summary);
      bar.append(inner);
      const spacer = document.createElement('div');
      spacer.className = 'eleat-desktop-purchase-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.hidden = true;
      // Body mounting avoids a transformed product/gallery ancestor trapping position:fixed.
      document.body.append(bar, spacer);

      let frame = 0;
      let disposed = false;
      let inView = true;
      const selectedCount = () => parseInt(text(root.querySelector('[data-progress-current]')), 10) || 0;
      const selectedLimit = () => parseInt(text(root.querySelector('[data-progress-limit]')).replace(/[^\d]/g, ''), 10) || 0;
      const eligible = () => desktop.matches && root.isConnected && shown(root) &&
        inView && selectedCount() > 0 && !flavour.classList.contains('hidden') && shown(flavour) && !overlayOpen();
      const unavailable = () => original.matches(':disabled, [aria-disabled="true"], [aria-busy="true"], .is-incomplete, .inactive, .loading, .is-loading, .pointer-events-none');

      function sync() {
        if (disposed) return;
        if (!root.isConnected) { destroy(); return; }
        const active = eligible();
        bar.hidden = !active;
        spacer.hidden = !active;
        const title = text(original.querySelector('[data-final-cta-title]')) ||
          original.getAttribute('aria-label') || text(original.querySelector('.cta-text'));
        setText(buttonLabel, title);
        button.disabled = !active || unavailable() || !title;
        setAttribute(button, 'aria-disabled', String(button.disabled));
        setAttribute(button, 'aria-busy', String(original.getAttribute('aria-busy') === 'true' || original.matches('.loading, .is-loading')));
        buttonIcons.forEach(({ source, icon }) => {
          if (button.getAttribute('aria-busy') !== 'true' && shown(source)) icon.removeAttribute('hidden');
          else setAttribute(icon, 'hidden', '');
        });
        const current = selectedCount();
        const limit = selectedLimit();
        setText(heading, `${current} / ${limit} packs added`);
        setAttribute(meter, 'aria-valuemax', String(limit));
        setAttribute(meter, 'aria-valuenow', String(current));
        const fillWidth = `${limit ? Math.min(100, current / limit * 100) : 0}%`;
        if (meterFill.style.width !== fillWidth) meterFill.style.width = fillWidth;
        const activePlan = plans.querySelector('.product-template__plan-option.active');
        setText(price, text(activePlan?.querySelector('.plan-option__price')));
        setText(compare, text(activePlan?.querySelector('.plan-option__compare-price')));
        compare.hidden = !compare.textContent || compare.textContent === price.textContent;
        const sourceSummary = root.querySelector('.eleat-purchase-summary');
        setText(summary, sourceSummary && !sourceSummary.hidden ? text(sourceSummary) : '');
        summary.hidden = !summary.textContent;
        if (active) {
          const height = `${Math.ceil(bar.getBoundingClientRect().height)}px`;
          if (spacer.style.height !== height) spacer.style.height = height;
          if (root.style.getPropertyValue('--eleat-desktop-purchase-height') !== height) {
            root.style.setProperty('--eleat-desktop-purchase-height', height);
          }
        } else {
          root.style.removeProperty('--eleat-desktop-purchase-height');
        }
        updateBodyState();
      }

      function schedule() {
        if (disposed || frame) return;
        frame = requestAnimationFrame(() => { frame = 0; sync(); });
      }

      button.addEventListener('click', () => {
        // Recheck current DOM state in case a plan/modal changed since the last frame.
        sync();
        if (button.disabled || bar.hidden || !eligible() || unavailable()) return;
        original.click();
        sync();
        schedule();
      });

      const productObserver = new MutationObserver(schedule);
      productObserver.observe(root, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-busy', 'aria-label', 'hidden', 'data-cta-action', 'data-plan-price', 'data-quantity']
      });
      const overlayObserver = new MutationObserver((records) => {
        if (records.some((record) => record.target instanceof Element &&
          (record.target === document.body || record.target === document.documentElement ||
            record.target.matches(overlaySelector) || record.target.closest(overlaySelector)))) schedule();
      });
      overlayObserver.observe(document.documentElement, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-modal']
      });
      const sizeObserver = new ResizeObserver(schedule);
      sizeObserver.observe(bar);
      const visibility = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        schedule();
      }, {rootMargin: '-120px 0px 0px 0px'});
      visibility.observe(root);
      desktop.addEventListener('change', schedule);
      window.addEventListener('resize', schedule, {passive: true});
      document.addEventListener('product-info-alt:set-plan', schedule);
      document.fonts?.ready.then(schedule);

      function destroy() {
        disposed = true;
        cancelAnimationFrame(frame);
        productObserver.disconnect();
        overlayObserver.disconnect();
        sizeObserver.disconnect();
        visibility.disconnect();
        desktop.removeEventListener('change', schedule);
        window.removeEventListener('resize', schedule);
        document.removeEventListener('product-info-alt:set-plan', schedule);
        bar.remove();
        spacer.remove();
        root.style.removeProperty('--eleat-desktop-purchase-height');
        controllers.delete(root);
        updateBodyState();
      }

      controllers.set(root, {active: () => !bar.hidden, destroy});
      sync();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, root) => { if (event.target.contains(root)) controller.destroy(); });
  });
})();
